import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useState } from "react";

export function useCommunitySignals() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isRunning, setIsRunning] = useState(false);

  // Fetch ingestion settings
  const {
    data: settings,
    isLoading: isLoadingSettings,
    refetch: refetchSettings
  } = useQuery({
    queryKey: ["community-ingestion-settings"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("community_ingestion_settings")
        .select("*")
        .limit(1)
        .single();

      if (error) throw error;
      return data;
    }
  });

  // Fetch sources
  const {
    data: sources,
    isLoading: isLoadingSources,
    refetch: refetchSources
  } = useQuery({
    queryKey: ["community-sources"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("community_sources")
        .select("*")
        .order("created_at", { ascending: true });

      if (error) throw error;
      return data;
    }
  });

  // Fetch signals with source info
  const {
    data: signals,
    isLoading: isLoadingSignals,
    refetch: refetchSignals
  } = useQuery({
    queryKey: ["community-signals"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("community_signals")
        .select(`
          *,
          community_sources (
            name,
            source_type
          )
        `)
        .order("created_at", { ascending: false })
        .limit(100);

      if (error) throw error;
      return data;
    }
  });

  // Run ingestion
  const runIngestion = async () => {
    setIsRunning(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        toast({
          title: "Authentication required",
          description: "Please sign in to run ingestion",
          variant: "destructive"
        });
        return;
      }

      const response = await supabase.functions.invoke("community-signals-ingest", {
        headers: {
          Authorization: `Bearer ${session.access_token}`
        }
      });

      if (response.error) {
        throw new Error(response.error.message);
      }

      const result = response.data;
      toast({
        title: "Ingestion complete",
        description: `Processed: ${result.processed}, Inserted: ${result.inserted}, Skipped: ${result.skipped}`
      });

      // Refetch all data
      await Promise.all([
        refetchSettings(),
        refetchSignals()
      ]);

    } catch (error) {
      toast({
        title: "Ingestion failed",
        description: error instanceof Error ? error.message : "Unknown error",
        variant: "destructive"
      });
    } finally {
      setIsRunning(false);
    }
  };

  const refetchAll = async () => {
    await Promise.all([
      refetchSettings(),
      refetchSources(),
      refetchSignals()
    ]);
  };

  return {
    settings,
    isLoadingSettings,
    sources,
    isLoadingSources,
    signals,
    isLoadingSignals,
    runIngestion,
    isRunning,
    refetchAll
  };
}
